import type { DocumentHandle } from '@platewell/core';

/**
 * The storage primitive both persistence adapters are built on: an append-only log of
 * document updates, periodically collapsed into one compacted entry.
 *
 * This shape is used rather than "save the whole document on every change" because writing
 * a multi-megabyte snapshot on every keystroke would make the app unusable on a phone.
 */
export interface UpdateLogStore {
  open(): Promise<void>;
  /** Updates in the order they were appended. Order matters: Yjs updates are causal. */
  readAll(): Promise<Uint8Array[]>;
  append(update: Uint8Array): Promise<void>;
  /** Atomically replaces the whole log with a single compacted entry. */
  replaceAll(compacted: Uint8Array): Promise<void>;
  count(): Promise<number>;
  close(): Promise<void>;
}

export interface PersistenceOptions {
  /** Compact once the log exceeds this many entries. */
  compactAfter?: number;
  onError?: (error: unknown) => void;
}

export interface Persistence {
  /** Resolves once the stored document has been loaded into the handle. */
  whenReady: Promise<void>;
  compact(): Promise<void>;
  destroy(): Promise<void>;
}

const DEFAULT_COMPACT_AFTER = 300;

/**
 * Wires a document handle to durable local storage.
 *
 * Attach this *before* anything writes to the document. Yjs updates from a single client
 * are causally ordered, so a log that begins midway leaves a gap and every later update
 * sits unapplied in the pending queue — the document would silently come back empty.
 */
export function attachPersistence(
  handle: DocumentHandle,
  store: UpdateLogStore,
  options: PersistenceOptions = {},
): Persistence {
  const compactAfter = options.compactAfter ?? DEFAULT_COMPACT_AFTER;
  const onError = options.onError ?? ((error: unknown) => console.error('persistence', error));

  let pending = 0;
  let destroyed = false;
  let queue: Promise<unknown> = Promise.resolve();

  const enqueue = <T>(task: () => Promise<T>): Promise<T> => {
    // Serialised so appends can never interleave with a compaction that is replacing the
    // very entries being appended to.
    const next = queue.then(task, task);
    queue = next.catch(onError);
    return next;
  };

  const whenReady = enqueue(async () => {
    await store.open();
    const updates = await store.readAll();
    for (const update of updates) handle.applyUpdate(update, 'load');
    pending = updates.length;
  });

  const unsubscribe = handle.onUpdate((update, origin) => {
    if (destroyed || origin === 'load') return;
    void enqueue(async () => {
      await store.append(update);
      pending += 1;
      if (pending > compactAfter) await compactNow();
    });
  });

  async function compactNow(): Promise<void> {
    await store.replaceAll(handle.encodeStateAsUpdate());
    pending = 1;
  }

  return {
    whenReady: whenReady.then(() => undefined),
    compact: () => enqueue(compactNow),
    destroy: async () => {
      destroyed = true;
      unsubscribe();
      await enqueue(() => store.close());
    },
  };
}
